/**
 * Agent Approval-history feature — Zod schemas.
 *
 * Wire contract with backend/internal/edge/approvaladmin. The admin OVERSIGHT log of agent
 * T3 approval decisions: who approved/denied which high-value agent action. READ-ONLY — a T3
 * approval is passkey-bound to the delegating human (WYSIWYS), so there is no decide action here.
 */

import { consequenceSchema, principalRefSchema } from "@pureid/auth-core";
import { z } from "zod";

export const approvalStatusSchema = z.enum([
  "pending",
  "approved",
  "denied",
  "expired",
  "consumed",
]);
export type ApprovalStatus = z.infer<typeof approvalStatusSchema>;

export const approvalHistoryRowSchema = z.object({
  approval_id: z.string(),
  // The CIBA handle the end-user portal keys its per-approval deep-link on. A still-pending row
  // links the delegating user straight to the passkey approval card (my.<rp_id>/approvals/<id>).
  // Navigation only — the admin still cannot decide here; only the delegating user can, on their passkey.
  auth_req_id: z.string(),
  agent_id: z.string(),
  agent_name: z.string().optional().default(""),
  // grant_jti deep-links to the grant's signed evidence chain (the agent detail evidence viewer).
  grant_jti: z.string(),
  // The approving human as the canonical typed principal (the only one who can sign — admins
  // observe only). Omitted when human_sub didn't resolve (UI falls back to human_sub).
  approver: principalRefSchema.optional(),
  human_sub: z.string(),
  summary: z.string().optional().default(""),
  // The generic, unit-agnostic consequence the action carried: a list of (dimension, quantity)
  // measurements (money "usd" is one dimension among many). Empty for a no-cost action. The
  // backend always emits the object (measurements omitempty), so default to an empty consequence.
  consequence: consequenceSchema.optional().default({ measurements: [] }),
  resource: z.string().optional().default(""),
  status: approvalStatusSchema,
  created_at: z.string(),
  decided_at: z.string().optional().default(""),
});
export type ApprovalHistoryRow = z.infer<typeof approvalHistoryRowSchema>;

export const approvalHistoryResponseSchema = z.object({
  approvals: z.array(approvalHistoryRowSchema),
  total: z.number().int(),
});
export type ApprovalHistoryResponse = z.infer<typeof approvalHistoryResponseSchema>;

// URL is the source of truth for the filter state. `days` scopes the analytics summary
// panel above the log (7/30/90; anything else falls back to the default) — the log rows
// themselves are the complete record, unwindowed. It also keeps old Trends deep links
// (?days=…) meaningful after their route folded into this page.
export const approvalHistorySearchSchema = z.object({
  status: approvalStatusSchema.optional(),
  offset: z.number().int().min(0).optional().default(0),
  days: z.coerce
    .number()
    .int()
    .refine((n) => [7, 30, 90].includes(n))
    .optional(),
});
export type ApprovalHistorySearch = z.infer<typeof approvalHistorySearchSchema>;
